import React from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { MdSearchOff } from "react-icons/md";

import styles from "./PromoDetails.module.scss";
import { usePromoDetails } from "./usePromoDetails";
import { PageHeader } from "../../../common/components/pageHeader/PageHeader";
import { EmptyState } from "../../../common/components/emptyState/EmptyState";
import { selectClusterList } from "../../../store/slices/clusterListSlice";
import {
  getClusterImageUrl,
  getFirstImage,
} from "../../../common/utils/functions/getImageUri";
import { clusterRoute } from "../../../common/utils/constants";

export const PromoDetails: React.FC = () => {
  const clusters = useSelector(selectClusterList);
  const { promo, ready } = usePromoDetails();

  if (!ready) return null;

  if (!promo) {
    return (
      <div className={styles.Page}>
        <EmptyState
          icon={MdSearchOff}
          title="Акция не найдена"
          description="Возможно, она была удалена или завершилась."
        />
      </div>
    );
  }

  const cluster = clusters.find((c) => c.id === promo.clusterId);

  // Берем первую картинку (без видео)
  const image = getFirstImage(promo.files);

  // Проверка на скидку
  const hasDiscount = promo.discount && promo.discount > 0;

  const externalPromoUrl = cluster
    ? `https://${cluster.domain}/promo/${promo.id}`
    : null;

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>{`${promo.name} — ЛОГМОЛЛ`}</title>
        <meta name="description" content={promo.description || promo.name} />
      </Helmet>

      <PageHeader title="Акция" subtitle={cluster?.name} />

      <div className={styles.Layout}>
        <div className={styles.ImageSide}>
          {image && cluster ? (
            <img
              src={getClusterImageUrl(cluster, image)}
              alt={promo.name}
              className={styles.MainImage}
            />
          ) : (
            <div className={styles.ImagePlaceholder} />
          )}

          {/* Бейдж только если есть скидка */}
          {hasDiscount && (
            <div className={styles.DiscountBadge}>-{promo.discount}%</div>
          )}
        </div>

        <div className={styles.InfoSide}>
          <h1 className={styles.Title}>{promo.name}</h1>

          {promo.description && (
            <p className={styles.Description}>{promo.description}</p>
          )}

          {promo.datesImportant && (
            <div className={styles.MetaBox}>
              <div className={styles.MetaRow}>
                <span className={styles.MetaLabel}>Действует до:</span>
                <span className={styles.MetaValue}>
                  {new Date(promo.endDate).toLocaleDateString("ru-RU")}
                </span>
              </div>
            </div>
          )}

          {cluster && (
            <div className={styles.ClusterBox}>
              <span className={styles.ClusterLabel}>Магазин:</span>
              <Link
                to={clusterRoute(cluster.id)}
                className={styles.ClusterLink}
              >
                <img
                  src={`https://${cluster.domain}/icons/logo512.png`}
                  alt={cluster.name}
                  className={styles.ClusterLogo}
                />
                <span className={styles.ClusterName}>{cluster.name}</span>
              </Link>
            </div>
          )}

          {externalPromoUrl && cluster && (
            <a
              href={externalPromoUrl}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={styles.ExternalLink}
            >
              Смотреть эту акцию в {cluster.name} →
            </a>
          )}

          <div className={styles.ActionsPlaceholder} />
        </div>
      </div>
    </div>
  );
};
